
const S = ({ className = "" }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

// ─── Mobile Header ────────────────────────────────────────────────────────────
export const MobileHeader = () => (
    <header className="lg:hidden sticky top-0 z-50 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <S className="w-32 h-9" />
        <div className="flex items-center gap-3">
            <div className="relative">
                <S className="w-7 h-7 rounded-full" />
                <S className="absolute -top-1 -right-1 w-4 h-4 rounded-full" />
            </div>
            <S className="w-7 h-7 rounded-full" />
        </div>
    </header>
);

// ─── Desktop Header ───────────────────────────────────────────────────────────
export const DesktopHeader = () => (
    <header className="hidden lg:flex sticky top-0 z-50 bg-white border-b border-gray-100 px-6 py-3 items-center justify-between gap-6">
        <S className="w-36 h-9 shrink-0" />
        <S className="flex-1 max-w-xl h-10 rounded-full" />
        <div className="flex items-center gap-4 shrink-0">
            <S className="w-7 h-7 rounded-full" />
            <S className="w-7 h-7 rounded-full" />
            <div className="relative">
                <S className="w-7 h-7 rounded-full" />
                <S className="absolute -top-1 -right-1 w-4 h-4 rounded-full" />
            </div>
            <S className="w-10 h-10 rounded-full" />
        </div>
    </header>
);

// ─── Left Sidebar (desktop only) ─────────────────────────────────────────────
const LeftSidebarSkeleton = () => (
    <aside className="hidden lg:flex w-60 shrink-0 flex-col gap-3">
        <div className="bg-white rounded-xl shadow-sm pb-5 overflow-visible">
            <S className="w-full h-28 rounded-t-xl rounded-b-none bg-orange-200" />
            <div className="flex justify-center -mt-9">
                <S className="w-[72px] h-[72px] rounded-full border-4 border-white" />
            </div>
            <div className="flex justify-center mt-3">
                <S className="w-40 h-4" />
            </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm px-4 py-3 flex items-center justify-between">
            <S className="w-24 h-3.5" />
            <S className="w-8 h-3.5" />
        </div>

        <div className="bg-white rounded-xl shadow-sm px-4 py-4 flex flex-col gap-4">
            {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                    <S className="w-5 h-5 rounded-sm" />
                    <S className="w-28 h-3.5" />
                </div>
            ))}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <S className="w-5 h-5 rounded-sm" />
                    <S className="w-20 h-3.5" />
                </div>
                <S className="w-3 h-3 rounded-sm" />
            </div>
        </div>
    </aside>
);

// ─── Right Sidebar (desktop only) ────────────────────────────────────────────
export const RightSidebarSkeleton = () => (
    <aside className="hidden lg:flex w-64 shrink-0 flex-col gap-3">
        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-4">
            <S className="w-24 h-4" />
            {[...Array(3)].map((_, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                    <S className="w-20 h-3" />
                    <S className="w-32 h-4" />
                    <S className="w-16 h-3" />
                </div>
            ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
            <S className="w-40 h-4" />
            <S className="w-full h-3" />
            <div className="flex justify-end">
                <S className="w-20 h-3.5" />
            </div>
        </div>

        <div className="mt-auto bg-white rounded-xl shadow-sm px-4 py-3 flex items-center justify-between">
            <S className="w-24 h-4" />
            <div className="flex gap-2">
                <S className="w-5 h-5 rounded-sm" />
                <S className="w-5 h-5 rounded-sm" />
            </div>
        </div>
    </aside>
);

// ─── Blog Post Card ───────────────────────────────────────────────────────────
const BlogCardSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm p-4 lg:p-5 flex flex-col gap-3 lg:gap-4">
        <S className="w-28 h-6 rounded-full" />
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
                <S className="w-10 h-10 rounded-full shrink-0" />
                <div className="flex flex-col gap-1.5">
                    <S className="w-36 h-3.5" />
                    <S className="w-20 h-3" />
                </div>
            </div>
            <S className="w-5 h-5 rounded-full" />
        </div>
        <S className="w-2/3 h-5" />
        <S className="w-full h-3.5" />
        <div className="flex gap-2">
            <S className="w-16 h-6 rounded-full" />
            <S className="w-14 h-6 rounded-full" />
        </div>
        <S className="w-full h-40 lg:h-48 rounded-lg" />
    </div>
);

// ─── Composer Card ────────────────────────────────────────────────────────────
const ComposerSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-4">
        <div className="flex items-center gap-3">
            <S className="w-10 h-10 rounded-full shrink-0" />
            <S className="flex-1 h-10 rounded-full" />
        </div>

        <div className="border-t border-gray-100" />

        <div className="flex lg:hidden items-center justify-around">
            {[...Array(4)].map((_, i) => (
                <S key={i} className="w-8 h-8 rounded-md" />
            ))}
        </div>

        <div className="hidden lg:flex items-center justify-around">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-2">
                    <S className="w-4 h-4 rounded-sm" />
                    <S className="w-20 h-3.5" />
                </div>
            ))}
        </div>
    </div>
);

// ─── Orange Sidebar Handle (mobile only) ─────────────────────────────────────
const SidebarHandle = () => (
    <div className="lg:hidden fixed left-0 top-1/2 -translate-y-1/2 z-40">
        <div className="w-4 h-10 bg-orange-400 rounded-r-full flex items-center justify-center">
            <div className="w-1.5 h-1.5 bg-white rounded-full" />
        </div>
    </div>
);

// ─── Bottom Nav (mobile only) ─────────────────────────────────────────────────
const BottomNavSkeleton = () => (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 px-6 py-3 flex items-center justify-around">
        <div className="flex flex-col items-center gap-1">
            <S className="w-6 h-6 rounded-md bg-orange-200" />
            <S className="w-10 h-2.5 bg-orange-200" />
        </div>
        <div className="flex flex-col items-center gap-1">
            <S className="w-6 h-6 rounded-full" />
            <S className="w-14 h-2.5" />
        </div>
        <div className="flex flex-col items-center gap-1">
            <S className="w-8 h-8 rounded-full" />
            <S className="w-12 h-2.5" />
        </div>
    </nav>
);

// ─── Root ─────────────────────────────────────────────────────────────────────
const HomePageSkeleton = () => (
    <div className="min-h-screen bg-gray-50">
        <MobileHeader />
        <DesktopHeader />
        <SidebarHandle />

        <div className="max-w-[1400px] mx-auto flex gap-4 px-3 lg:px-4 pt-4 pb-20 lg:pb-8 items-start">
            <LeftSidebarSkeleton />

            <main className="flex-1 min-w-0 flex gap-3">
                <div className="flex-1 flex flex-col gap-3">
                    <ComposerSkeleton />
                    <BlogCardSkeleton />
                    <BlogCardSkeleton />
                </div>
                <div className="hidden lg:block w-1 bg-orange-400 rounded-full self-stretch shrink-0 mx-1" />
            </main>

            <RightSidebarSkeleton />
        </div>

        <BottomNavSkeleton />
    </div>
);

export default HomePageSkeleton;